import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import LandingPage from './LandingPage';
import LoginPage from './LoginPage';
import EligibilityPage from './EligibilityPage';
import Dashboard from './Dashboard';
import AdminDashboard from './AdminDashboard';
import LoanApplication from './LoanApplication';
import TroubleLoginPage from './TroubleLoginPage';
import ESignPage from './ESignPage';
import PaymentSuccess from './PaymentSuccess';
import PaymentFailure from './PaymentFailure';
import TermsAndCondition from './TermsAndCondition';
import PrivacyPolicy from './PrivacyPolicy';
import DigilockerClosePage from './DigilockerClosePage';
import SessionManager from './SessionManager';
import ErrorBoundary from './ErrorBoundary';
import ProtectedRoute from './ProtectedRoute';
import KioskHeartbeat from './KioskHeartbeat';
import VirtualKeyboard from './VirtualKeyboard';
import { LanguageProvider } from './languageContext';
import './App.css';

const PROTECTED_PATHS = new Set(['/dashboard', '/apply-loan', '/esign']);

const hasActiveUserSession = () => {
  try {
    const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
    const token = String(localStorage.getItem('token') || '').trim();
    return Boolean(storedUser && typeof storedUser === 'object' && Object.keys(storedUser).length > 0 && token);
  } catch (_error) {
    return false;
  }
};

const isProtectedPath = (pathName) => PROTECTED_PATHS.has(String(pathName || '').trim());

function App() {
  useEffect(() => {
    document.documentElement.classList.add('secure-kiosk-mode');

    const blockEvent = (event) => {
      event.preventDefault();
    };

    const blockShortcut = (event) => {
      const key = String(event.key || '').toLowerCase();
      if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'x', 'p', 's', 'u'].includes(key)) {
        event.preventDefault();
      }
    };

    const clearSelection = () => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0 && String(selection).trim()) {
        selection.removeAllRanges();
      }

      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA') &&
        typeof activeElement.selectionStart === 'number' &&
        typeof activeElement.selectionEnd === 'number' &&
        activeElement.selectionStart !== activeElement.selectionEnd
      ) {
        activeElement.setSelectionRange(activeElement.selectionEnd, activeElement.selectionEnd);
      }
    };

    document.addEventListener('copy', blockEvent);
    document.addEventListener('cut', blockEvent);
    document.addEventListener('contextmenu', blockEvent);
    document.addEventListener('dragstart', blockEvent);
    document.addEventListener('selectstart', blockEvent);
    document.addEventListener('keydown', blockShortcut, true);
    document.addEventListener('selectionchange', clearSelection);

    return () => {
      document.documentElement.classList.remove('secure-kiosk-mode');
      document.removeEventListener('copy', blockEvent);
      document.removeEventListener('cut', blockEvent);
      document.removeEventListener('contextmenu', blockEvent);
      document.removeEventListener('dragstart', blockEvent);
      document.removeEventListener('selectstart', blockEvent);
      document.removeEventListener('keydown', blockShortcut, true);
      document.removeEventListener('selectionchange', clearSelection);
    };
  }, []);

  useEffect(() => {
    const enforceSessionGuard = () => {
      const pathName = window.location.pathname;
      if (!isProtectedPath(pathName)) return;
      if (hasActiveUserSession()) return;
      window.location.replace('/login');
    };

    const handlePageShow = () => {
      enforceSessionGuard();
    };

    const handlePopState = () => {
      enforceSessionGuard();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        enforceSessionGuard();
      }
    };

    enforceSessionGuard();
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const recordRuntimeError = (label, error) => {
      const payload = {
        label,
        message: String(error?.message || error?.reason || error || 'Unknown error'),
        stack: String(error?.stack || ''),
        at: new Date().toISOString()
      };

      try {
        localStorage.setItem('kiosk_last_runtime_error', JSON.stringify(payload));
      } catch (_storageError) {
        // Best effort only.
      }

      console.error(`[${label}]`, payload.message, payload.stack);
    };

    const handleWindowError = (event) => {
      recordRuntimeError('window.error', event?.error || event?.message || 'Unknown window error');
    };

    const handleUnhandledRejection = (event) => {
      recordRuntimeError('unhandledrejection', event?.reason || 'Unknown rejection');
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  if (window.location.pathname === '/digilocker-close') {
    return <DigilockerClosePage />;
  }

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <Router>
          <SessionManager />
          <KioskHeartbeat />
          <VirtualKeyboard />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/check-eligibility" element={<EligibilityPage />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/apply-loan" element={<ProtectedRoute><LoanApplication /></ProtectedRoute>} />
            <Route path="/esign" element={<ProtectedRoute><ESignPage /></ProtectedRoute>} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-failure" element={<PaymentFailure />} />
            <Route path="/digilocker-close" element={<DigilockerClosePage />} />
            <Route path="/trouble-login" element={<TroubleLoginPage />} />
            <Route path="/terms-and-conditions" element={<TermsAndCondition />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          </Routes>
        </Router>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
